/**
 * Voltide — Billing & Invoicing (new module, Phase A).
 *
 * Replaces the older contract-driven cycle UI. All data flows through
 * /api/invoicing/*. Supports:
 *   - Invoice list with filters (client, status, type, period)
 *   - Create Adhoc invoice
 *   - Run Monthly Batch (admin)
 *   - Voltide settings (gear → modal)
 *   - View / edit (if not approved) / approve / generate PDF / send / void / mark paid
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { invoicingAPI, clientsAPI } from '../utils/api';
import {
  Plus, Settings, RefreshCw, Eye, Send, FileText, Lock, CheckCircle, XCircle,
  Trash, Download, Calendar, AlertCircle, AlertTriangle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const STATUS_STYLES = {
  draft:            'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-50 text-amber-800',
  approved:         'bg-blue-50 text-blue-800',
  sent:             'bg-emerald-50 text-emerald-800',
  paid:             'bg-emerald-100 text-emerald-900 font-semibold',
  void:             'bg-red-50 text-red-800 line-through',
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const BillingPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ client_id: '', status: '', invoice_type: '', year: '', month: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showRunMonthly, setShowRunMonthly] = useState(false);
  const [selectedInv, setSelectedInv] = useState(null);
  const [showSend, setShowSend] = useState(false);
  const [showVoid, setShowVoid] = useState(false);
  const [currentLegalName, setCurrentLegalName] = useState('');
  const [regenBusy, setRegenBusy] = useState({});  // {invoice_id: true}
  const { isAdmin } = useAuth();

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await invoicingAPI.listInvoices(params);
      setInvoices(r.data?.rows || []);
    } catch (e) { toast.error('Failed to load invoices'); }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    (async () => {
      try { const c = await clientsAPI.getAll(); setClients(c.data || []); } catch (e) { console.error('getAll clients failed', e); }
      try {
        const s = await invoicingAPI.getSettings();
        setCurrentLegalName(s.data?.voltide_legal_name || '');
      } catch (e) { console.error('getSettings failed', e); }
    })();
  }, []);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const clientName = (id) => clients.find(c => c.client_id === id)?.name || id?.slice(0, 8);

  // Stale-seller-name helpers (legal entity audit).
  const isStaleSellerName = (inv) => {
    const stored = inv?.seller?.legal_name;
    return Boolean(stored && currentLegalName && stored !== currentLegalName);
  };
  const handleRegenStaleName = async (inv, e) => {
    e?.stopPropagation?.();
    setRegenBusy(prev => ({ ...prev, [inv.invoice_id]: true }));
    try {
      await invoicingAPI.generatePdf(inv.invoice_id);
      toast.success(`PDF regenerated with current legal name (${inv.invoice_number})`);
      // Reload the row only — cheaper than full list reload
      const r = await invoicingAPI.getInvoice(inv.invoice_id);
      setInvoices(rows => rows.map(x => x.invoice_id === inv.invoice_id ? r.data : x));
    } catch (err) {
      toast.error(err.userMessage || err.response?.data?.detail || 'Regenerate failed');
    } finally {
      setRegenBusy(prev => { const n = { ...prev }; delete n[inv.invoice_id]; return n; });
    }
  };

  const handleApprove = async (inv) => {
    try { await invoicingAPI.approve(inv.invoice_id); toast.success('Approved'); loadInvoices(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
  };
  const handleRequestApproval = async (inv) => {
    try { await invoicingAPI.requestApproval(inv.invoice_id); toast.success('Sent for approval'); loadInvoices(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleGeneratePdf = async (inv) => {
    try {
      await invoicingAPI.generatePdf(inv.invoice_id);
      toast.success('PDF ready');
      await invoicingAPI.openPdf(inv.invoice_id);
    } catch (e) { toast.error('PDF generation failed'); }
  };
  const handleMarkPaid = async (inv) => {
    try { await invoicingAPI.markPaid(inv.invoice_id); toast.success('Marked paid'); loadInvoices(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="billing-page">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 md:p-8 max-w-[1920px] mx-auto space-y-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <PageHeader title="Billing & Invoicing" description="All invoices across clients · Fixed monthly + Adhoc" />
            <div className="flex gap-2">
              {isAdmin && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="open-settings-btn">
                    <Settings className="w-4 h-4 mr-1.5" /> Voltide Settings
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowRunMonthly(true)} data-testid="run-monthly-btn">
                    <Calendar className="w-4 h-4 mr-1.5" /> Run Monthly Batch
                  </Button>
                </>
              )}
              <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-adhoc-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Create Adhoc Invoice
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-2 flex-wrap" data-testid="filters-bar">
            <select value={filters.client_id} onChange={e => setFilters(f => ({ ...f, client_id: e.target.value }))}
              data-testid="filter-client" className="text-xs border border-slate-200 rounded-sm px-2 py-1.5 bg-white">
              <option value="">All clients</option>
              {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
            </select>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              data-testid="filter-status" className="text-xs border border-slate-200 rounded-sm px-2 py-1.5 bg-white">
              <option value="">All status</option>
              {['draft','pending_approval','approved','sent','paid','void'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.invoice_type} onChange={e => setFilters(f => ({ ...f, invoice_type: e.target.value }))}
              data-testid="filter-type" className="text-xs border border-slate-200 rounded-sm px-2 py-1.5 bg-white">
              <option value="">All types</option>
              <option value="monthly">Monthly</option>
              <option value="adhoc">Adhoc</option>
            </select>
            <Input type="number" placeholder="Year" value={filters.year} onChange={e => setFilters(f => ({ ...f, year: e.target.value }))}
              data-testid="filter-year" className="h-8 text-xs w-24" />
            <Input type="number" placeholder="Month" value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))}
              data-testid="filter-month" className="h-8 text-xs w-24" min={1} max={12} />
            <Button size="sm" variant="ghost" onClick={loadInvoices} className="h-8" data-testid="refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <span className="ml-auto text-[11px] text-slate-500">{invoices.length} invoice(s)</span>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            {loading ? <LoadingSpinner /> : (
              <table className="w-full text-xs" data-testid="invoices-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-500">
                    <th className="px-3 py-2 text-left font-medium">Invoice #</th>
                    <th className="px-3 py-2 text-left font-medium">Client</th>
                    <th className="px-3 py-2 text-left font-medium">Type</th>
                    <th className="px-3 py-2 text-left font-medium">Period</th>
                    <th className="px-3 py-2 text-left font-medium">Issue / Due</th>
                    <th className="px-3 py-2 text-right font-medium">Total</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {invoices.length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-400">No invoices match the current filters.</td></tr>
                  ) : invoices.map(inv => (
                    <tr key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`} className="hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-mono">
                        <span className="inline-flex items-center gap-1.5">
                          {inv.invoice_number}
                          {isStaleSellerName(inv) && (
                            <button
                              type="button"
                              onClick={(e) => handleRegenStaleName(inv, e)}
                              disabled={!!regenBusy[inv.invoice_id]}
                              data-testid={`stale-name-${inv.invoice_id}`}
                              title={`Seller name on this invoice ("${inv.seller?.legal_name}") differs from current settings ("${currentLegalName}"). Click to regenerate PDF.`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-sans font-medium hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {regenBusy[inv.invoice_id]
                                ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                : <AlertTriangle className="w-2.5 h-2.5" />}
                              <span>Stale name</span>
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2">{clientName(inv.client_id)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${inv.invoice_type === 'monthly' ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-800'}`}>{inv.invoice_type}</span>
                      </td>
                      <td className="px-3 py-2 text-[11px]">{inv.invoice_period?.from_date ? `${inv.invoice_period.from_date} → ${inv.invoice_period.to_date}` : '—'}</td>
                      <td className="px-3 py-2 text-[11px]">{inv.issue_date}<br/><span className="text-slate-400">due {inv.due_date}</span></td>
                      <td className="px-3 py-2 text-right font-mono">{fmtINR(inv.total_inr)}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_STYLES[inv.status] || ''}`} data-testid={`status-${inv.invoice_id}`}>{inv.status}</span></td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button onClick={() => setSelectedInv(inv)} title="View / Edit" data-testid={`view-${inv.invoice_id}`} className="p-1 rounded hover:bg-slate-100"><Eye className="w-3.5 h-3.5 text-slate-600" /></button>
                          {inv.status === 'draft' && <button onClick={() => handleRequestApproval(inv)} title="Request Approval" data-testid={`req-${inv.invoice_id}`} className="p-1 rounded hover:bg-amber-50"><AlertCircle className="w-3.5 h-3.5 text-amber-600" /></button>}
                          {(inv.status === 'draft' || inv.status === 'pending_approval') && isAdmin && <button onClick={() => handleApprove(inv)} title="Approve" data-testid={`approve-${inv.invoice_id}`} className="p-1 rounded hover:bg-blue-50"><Lock className="w-3.5 h-3.5 text-blue-600" /></button>}
                          <button onClick={() => handleGeneratePdf(inv)} title="Generate / Download PDF" data-testid={`pdf-${inv.invoice_id}`} className="p-1 rounded hover:bg-slate-100"><FileText className="w-3.5 h-3.5 text-slate-700" /></button>
                          {(inv.status === 'approved' || inv.status === 'sent') && isAdmin && <button onClick={() => { setSelectedInv(inv); setShowSend(true); }} title="Send via Titan" data-testid={`send-${inv.invoice_id}`} className="p-1 rounded hover:bg-emerald-50"><Send className="w-3.5 h-3.5 text-emerald-600" /></button>}
                          {(inv.status === 'sent' || inv.status === 'approved') && isAdmin && <button onClick={() => handleMarkPaid(inv)} title="Mark Paid" data-testid={`paid-${inv.invoice_id}`} className="p-1 rounded hover:bg-emerald-50"><CheckCircle className="w-3.5 h-3.5 text-emerald-700" /></button>}
                          {inv.status !== 'void' && inv.status !== 'paid' && isAdmin && <button onClick={() => { setSelectedInv(inv); setShowVoid(true); }} title="Void" data-testid={`void-${inv.invoice_id}`} className="p-1 rounded hover:bg-red-50"><XCircle className="w-3.5 h-3.5 text-red-600" /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showCreate && <CreateAdhocInvoiceModal clients={clients} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); loadInvoices(); }} />}
      {showSettings && <VoltideSettingsModal onClose={() => setShowSettings(false)} />}
      {showRunMonthly && <RunMonthlyBatchModal onClose={() => setShowRunMonthly(false)} onDone={loadInvoices} />}
      {selectedInv && !showSend && !showVoid && <InvoiceDetailModal inv={selectedInv} isAdmin={isAdmin} onClose={() => setSelectedInv(null)} onSaved={() => { setSelectedInv(null); loadInvoices(); }} onSend={() => setShowSend(true)} onVoid={() => setShowVoid(true)} onReload={loadInvoices} />}
      {showSend && selectedInv && <SendInvoiceModal inv={selectedInv} clients={clients} onClose={() => { setShowSend(false); setSelectedInv(null); }} onSent={() => { setShowSend(false); setSelectedInv(null); loadInvoices(); }} />}
      {showVoid && selectedInv && <VoidInvoiceModal inv={selectedInv} onClose={() => { setShowVoid(false); setSelectedInv(null); }} onDone={() => { setShowVoid(false); setSelectedInv(null); loadInvoices(); }} />}
    </div>
  );
};

// ---------- Modals ----------

function CreateAdhocInvoiceModal({ clients, onClose, onSaved }) {
  const [clientId, setClientId] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ _key: `li-${Date.now()}`, description: '', sac_code: '', quantity: 1, unit: 'service', rate_ex_gst: 0, amount_ex_gst: 0 }]);
  const [saving, setSaving] = useState(false);

  const updateItem = (i, patch) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, ...patch, amount_ex_gst: (patch.rate_ex_gst ?? it.rate_ex_gst) * (patch.quantity ?? it.quantity) } : it));

  const save = async () => {
    if (!clientId) return toast.error('Select a client');
    if (items.some(li => !li.description || !li.rate_ex_gst)) return toast.error('Each line item needs description + rate');
    setSaving(true);
    try {
      await invoicingAPI.createAdhoc({ client_id: clientId, issue_date: issueDate, due_date: dueDate, line_items: items.map(({ _key, ...li }) => li), notes });
      toast.success('Adhoc invoice created (draft)');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Adhoc Invoice</DialogTitle>
          <DialogDescription>One-off charges. Created as <b>draft</b>; you can edit until approval.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Client</Label>
              <select value={clientId} onChange={e => setClientId(e.target.value)} data-testid="adhoc-client" className="w-full mt-1 text-sm border border-slate-200 rounded-sm px-2 py-1.5 bg-white">
                <option value="">Select…</option>
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Issue Date</Label><Input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} data-testid="adhoc-issue" /></div>
            <div><Label className="text-xs">Due Date</Label><Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="adhoc-due" /></div>
          </div>
          <div>
            <Label className="text-xs">Line Items</Label>
            {items.map((it, i) => (
              <div key={it._key || i} className="grid grid-cols-12 gap-2 mt-1 items-center">
                <Input placeholder="Description" value={it.description} onChange={e => updateItem(i, { description: e.target.value })} className="col-span-4 h-8 text-xs" data-testid={`li-desc-${i}`} />
                <Input placeholder="SAC" value={it.sac_code} onChange={e => updateItem(i, { sac_code: e.target.value })} className="col-span-2 h-8 text-xs" />
                <Input type="number" placeholder="Qty" value={it.quantity} onChange={e => updateItem(i, { quantity: Number(e.target.value) || 0 })} className="col-span-1 h-8 text-xs" />
                <Input placeholder="Unit" value={it.unit} onChange={e => updateItem(i, { unit: e.target.value })} className="col-span-1 h-8 text-xs" />
                <Input type="number" placeholder="Rate ex-GST" value={it.rate_ex_gst} onChange={e => updateItem(i, { rate_ex_gst: Number(e.target.value) || 0 })} className="col-span-2 h-8 text-xs" data-testid={`li-rate-${i}`} />
                <span className="col-span-1 text-[11px] text-right font-mono">{fmtINR(it.amount_ex_gst)}</span>
                <button onClick={() => setItems(arr => arr.filter((_, idx) => idx !== i))} className="col-span-1 text-slate-400 hover:text-red-600"><Trash className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setItems(arr => [...arr, { _key: `li-${Date.now()}-${arr.length}`, description: '', sac_code: '', quantity: 1, unit: 'service', rate_ex_gst: 0, amount_ex_gst: 0 }])} className="mt-2 h-7 text-xs" data-testid="add-line-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add line
            </Button>
          </div>
          <div><Label className="text-xs">Notes (internal)</Label><Input value={notes} onChange={e => setNotes(e.target.value)} className="h-8 text-xs" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="adhoc-save-btn">{saving ? 'Saving…' : 'Create Invoice'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunMonthlyBatchModal({ onClose, onDone }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await invoicingAPI.runMonthlyBatch(year, month);
      setResult(r.data);
      toast.success(`Created ${r.data?.created?.length || 0} · Skipped ${r.data?.skipped_idempotent?.length || 0}`);
      onDone();
    } catch (e) { toast.error('Batch failed'); }
    setRunning(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Run Monthly Batch</DialogTitle>
          <DialogDescription>Creates draft monthly invoices for every client with active commercial terms. Idempotent — re-running will not create duplicates.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Year</Label><Input type="number" value={year} onChange={e => setYear(Number(e.target.value))} data-testid="batch-year" /></div>
          <div><Label className="text-xs">Month</Label><Input type="number" min={1} max={12} value={month} onChange={e => setMonth(Number(e.target.value))} data-testid="batch-month" /></div>
        </div>
        {result && (
          <div className="mt-3 p-3 bg-slate-50 rounded text-xs space-y-1">
            <div>Created: <b>{result.created?.length || 0}</b> ({(result.created || []).join(', ') || '—'})</div>
            <div>Skipped (already existed): <b>{result.skipped_idempotent?.length || 0}</b></div>
            <div>Failed: <b>{result.failed?.length || 0}</b> {(result.failed || []).map(f => f.error).join(' · ')}</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={run} disabled={running} data-testid="batch-run-btn">{running ? 'Running…' : 'Run'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoltideSettingsModal({ onClose }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { invoicingAPI.getSettings().then(r => setS(r.data)).catch(() => toast.error('Failed to load settings')); }, []);
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try { await invoicingAPI.updateSettings(s); toast.success('Settings saved'); onClose(); }
    catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  const Field = ({ k, label, type = 'text' }) => (
    <div><Label className="text-xs">{label}</Label>
      <Input type={type} value={s[k] ?? ''} onChange={e => set(k, type === 'number' ? Number(e.target.value) : e.target.value)} data-testid={`settings-${k}`} className="h-8 text-xs" />
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Voltide Seller Settings</DialogTitle><DialogDescription>Frozen onto each invoice at creation time. Only super_admin/admin can edit.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field k="voltide_legal_name" label="Legal Name" />
          <Field k="voltide_gstin" label="GSTIN" />
          <Field k="voltide_pan" label="PAN" />
          <Field k="voltide_state_code" label="State Code (2 digit)" />
          <Field k="voltide_state" label="State" />
          <Field k="voltide_city" label="City" />
          <Field k="voltide_address_line1" label="Address Line 1" />
          <Field k="voltide_address_line2" label="Address Line 2" />
          <Field k="voltide_pincode" label="Pincode" />
          <Field k="voltide_email" label="Email" />
          <Field k="voltide_phone" label="Phone" />
          <Field k="invoice_prefix" label="Invoice Prefix" />
          <Field k="default_gst_rate" label="Default GST Rate (decimal)" type="number" />
          <Field k="default_sac_code" label="Default SAC Code" />
          <Field k="voltide_bank_name" label="Bank Name" />
          <Field k="voltide_account_number" label="Account #" />
          <Field k="voltide_ifsc" label="IFSC" />
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="settings-save-btn">{saving ? 'Saving…' : 'Save'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDetailModal({ inv: initial, isAdmin, onClose, onSaved, onSend, onVoid, onReload }) {
  const [inv, setInv] = useState(initial);
  const editable = inv.status === 'draft' || inv.status === 'pending_approval';
  const [items, setItems] = useState(inv.line_items || []);
  const [issueDate, setIssueDate] = useState(inv.issue_date);
  const [dueDate, setDueDate] = useState(inv.due_date);
  const [notes, setNotes] = useState(inv.notes || '');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const updateItem = (i, patch) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, ...patch, amount_ex_gst: (patch.rate_ex_gst ?? it.rate_ex_gst) * (patch.quantity ?? it.quantity) } : it));

  const save = async () => {
    setSaving(true);
    try {
      const r = await invoicingAPI.updateInvoice(inv.invoice_id, { line_items: items, issue_date: issueDate, due_date: dueDate, notes });
      setInv(r.data);
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const refresh = async () => {
    try { const r = await invoicingAPI.getInvoice(inv.invoice_id); setInv(r.data); onReload && onReload(); } catch (e) { console.error('refresh invoice failed', e); }
  };
  const doTransition = async (action) => {
    setBusy(true);
    try {
      if (action === 'request') await invoicingAPI.requestApproval(inv.invoice_id);
      else if (action === 'approve') await invoicingAPI.approve(inv.invoice_id);
      else if (action === 'paid') await invoicingAPI.markPaid(inv.invoice_id);
      toast.success('Updated');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const handlePdf = async () => {
    setBusy(true);
    try {
      await invoicingAPI.generatePdf(inv.invoice_id);
      await invoicingAPI.openPdf(inv.invoice_id);
    } catch (e) { toast.error('PDF generation failed'); }
    setBusy(false);
  };
  const handlePreview = async () => {
    setBusy(true);
    try { await invoicingAPI.openPreview(inv.invoice_id); }
    catch (e) { toast.error('Preview failed'); }
    setBusy(false);
  };

  // What's allowed by the backend state machine
  const can = {
    edit:    inv.status === 'draft' || inv.status === 'pending_approval',
    request: inv.status === 'draft',
    approve: (inv.status === 'draft' || inv.status === 'pending_approval') && isAdmin,
    send:    (inv.status === 'approved' || inv.status === 'sent') && isAdmin,
    paid:    (inv.status === 'approved' || inv.status === 'sent') && isAdmin,
    voidIt:  inv.status !== 'void' && inv.status !== 'paid' && isAdmin,
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{inv.invoice_number} <span className="text-xs text-slate-500 font-normal">· {inv.invoice_type} · <span className={`px-1.5 py-0.5 rounded ${STATUS_STYLES[inv.status]}`}>{inv.status}</span></span></DialogTitle>
          <DialogDescription>{inv.billed_to?.legal_name} · {fmtINR(inv.total_inr)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Issue Date</Label><Input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} disabled={!editable} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Due Date</Label><Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} disabled={!editable} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">FY · POS</Label><Input value={`${inv.financial_year} · ${inv.place_of_supply_state || '—'}`} disabled className="h-8 text-xs" /></div>
        </div>
        <div className="mt-2">
          <table className="w-full text-xs border border-slate-200">
            <thead><tr className="bg-slate-50 text-slate-500"><th className="p-1.5 text-left">Description</th><th className="p-1.5">SAC</th><th className="p-1.5">Qty</th><th className="p-1.5">Unit</th><th className="p-1.5 text-right">Rate</th><th className="p-1.5 text-right">Amount</th></tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={`${it.description || ''}-${it.sac_code || ''}-${i}`} className="border-t">
                  <td className="p-1"><Input value={it.description} onChange={e => updateItem(i, { description: e.target.value })} disabled={!editable} className="h-7 text-xs" /></td>
                  <td className="p-1"><Input value={it.sac_code} onChange={e => updateItem(i, { sac_code: e.target.value })} disabled={!editable} className="h-7 text-xs w-20" /></td>
                  <td className="p-1"><Input type="number" value={it.quantity} onChange={e => updateItem(i, { quantity: Number(e.target.value) || 0 })} disabled={!editable} className="h-7 text-xs w-16" /></td>
                  <td className="p-1"><Input value={it.unit} onChange={e => updateItem(i, { unit: e.target.value })} disabled={!editable} className="h-7 text-xs w-20" /></td>
                  <td className="p-1 text-right"><Input type="number" value={it.rate_ex_gst} onChange={e => updateItem(i, { rate_ex_gst: Number(e.target.value) || 0 })} disabled={!editable} className="h-7 text-xs w-28" /></td>
                  <td className="p-1 text-right font-mono">{fmtINR(it.amount_ex_gst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-right text-xs space-y-0.5">
            <div>Subtotal (ex-GST): <b>{fmtINR(inv.subtotal_ex_gst)}</b></div>
            {inv.gst_breakup_type === 'CGST_SGST' ? (
              <>
                <div>CGST {((inv.cgst_rate || 0) * 100).toFixed(2)}%: <b>{fmtINR(inv.cgst_amount)}</b></div>
                <div>SGST {((inv.sgst_rate || 0) * 100).toFixed(2)}%: <b>{fmtINR(inv.sgst_amount)}</b></div>
              </>
            ) : (
              <div>IGST {((inv.igst_rate || 0) * 100).toFixed(2)}%: <b>{fmtINR(inv.igst_amount)}</b></div>
            )}
            <div className="text-base">Total: <b>{fmtINR(inv.total_inr)}</b></div>
          </div>
        </div>
        <div><Label className="text-xs">Notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} disabled={!editable} className="h-8 text-xs" /></div>

        {/* Status action strip — always visible so the workflow + email step is discoverable */}
        <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-sm" data-testid="invoice-actions-strip">
          <div className="text-[10px] font-medium tracking-wider uppercase text-slate-500 mb-2">Workflow · current status: <span className="text-slate-900">{inv.status}</span></div>
          <div className="flex flex-wrap gap-2">
            {/* Request approval */}
            <Button size="sm" variant="outline" disabled={!can.request || busy} onClick={() => doTransition('request')} title={can.request ? 'Move from draft → pending approval' : 'Only available in draft'} data-testid="modal-request-btn">
              <AlertCircle className="w-3.5 h-3.5 mr-1" /> Request Approval
            </Button>
            {/* Approve */}
            <Button size="sm" variant="outline" disabled={!can.approve || busy} onClick={() => doTransition('approve')} title={can.approve ? 'Approve & lock content' : isAdmin ? 'Already approved' : 'Admin only'} data-testid="modal-approve-btn">
              <Lock className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
            {/* Send via email — the "missing" feature */}
            <Button size="sm" disabled={!can.send || busy} onClick={() => onSend && onSend()} title={can.send ? 'Email this invoice via Titan SMTP from ops@voltide.energy' : 'Approve the invoice first to enable Send'} data-testid="modal-send-btn" className={can.send ? 'bg-emerald-600 hover:bg-emerald-700' : ''}>
              <Send className="w-3.5 h-3.5 mr-1" /> Send via Email
            </Button>
            {/* Mark paid */}
            <Button size="sm" variant="outline" disabled={!can.paid || busy} onClick={() => doTransition('paid')} title={can.paid ? 'Mark this invoice as paid' : 'Only after approval'} data-testid="modal-paid-btn">
              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Mark Paid
            </Button>
            {/* Void */}
            <Button size="sm" variant="outline" disabled={!can.voidIt || busy} onClick={() => onVoid && onVoid()} title={can.voidIt ? 'Void this invoice (irreversible; reason required)' : ''} data-testid="modal-void-btn" className={can.voidIt ? 'text-red-600 border-red-200 hover:bg-red-50' : ''}>
              <XCircle className="w-3.5 h-3.5 mr-1" /> Void
            </Button>
          </div>
          {!can.send && (inv.status === 'draft' || inv.status === 'pending_approval') && (
            <div className="mt-2 text-[10px] text-slate-500">
              <b>Tip:</b> emailing is enabled once the invoice is <b>approved</b>. Click <b>Approve</b> above (admin) to unlock <b>Send via Email</b>.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={handlePreview} disabled={busy} data-testid="modal-preview-btn"><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
          <Button variant="ghost" onClick={handlePdf} disabled={busy} data-testid="modal-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {editable && <Button onClick={save} disabled={saving} data-testid="invoice-save-btn">{saving ? 'Saving…' : 'Save Changes'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendInvoiceModal({ inv, clients, onClose, onSent }) {
  const client = clients.find(c => c.client_id === inv.client_id);
  const defaultTo = useMemo(() => client?.contact_email ? [client.contact_email] : [], [client]);
  const [to, setTo] = useState(defaultTo.join(', '));
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(`Invoice ${inv.invoice_number} | ${inv.billed_to?.legal_name}`);
  const [bodyHtml, setBodyHtml] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    invoicingAPI.getBillingProfile(inv.client_id).then(r => {
      const prefs = r.data?.invoice_preferences || {};
      const accountants = (r.data?.contacts || []).filter(c => c.role === 'accountant').map(c => c.email).filter(Boolean);
      const toList = [...new Set([...(prefs.send_to_emails || []), ...accountants, ...defaultTo])];
      if (toList.length) setTo(toList.join(', '));
      if (prefs.cc_emails?.length) setCc(prefs.cc_emails.join(', '));
    }).catch(() => {});
    // eslint-disable-next-line
  }, [inv.client_id]);

  const send = async () => {
    const toArr = to.split(/[,;]\s*/).filter(Boolean);
    const ccArr = cc.split(/[,;]\s*/).filter(Boolean);
    if (!toArr.length) return toast.error('Add at least one recipient');
    setSending(true);
    try {
      await invoicingAPI.sendEmail(inv.invoice_id, { to_emails: toArr, cc_emails: ccArr, subject, body_html: bodyHtml || undefined });
      toast.success('Sent via Titan');
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Send Invoice {inv.invoice_number}</DialogTitle><DialogDescription>From <b>{inv.seller?.email || 'ops@voltide.energy'}</b> · Attaches generated PDF</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">To (comma-separated)</Label><Input value={to} onChange={e => setTo(e.target.value)} data-testid="send-to" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">CC</Label><Input value={cc} onChange={e => setCc(e.target.value)} data-testid="send-cc" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Subject</Label><Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="send-subject" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Body HTML override (optional)</Label>
            <textarea value={bodyHtml} onChange={e => setBodyHtml(e.target.value)} rows={5} className="w-full text-xs border border-slate-200 rounded-sm p-2 font-mono" placeholder="Leave blank to use the default branded body" />
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={send} disabled={sending} data-testid="send-confirm-btn">{sending ? 'Sending…' : <><Send className="w-3.5 h-3.5 mr-1" /> Send</>}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoidInvoiceModal({ inv, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const submit = async () => {
    if (!reason) return toast.error('Reason required');
    setVoiding(true);
    try { await invoicingAPI.voidInvoice(inv.invoice_id, reason); toast.success('Voided'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Void failed'); }
    setVoiding(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Void {inv.invoice_number}?</DialogTitle><DialogDescription>This is final. Provide a reason for the audit trail.</DialogDescription></DialogHeader>
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. duplicate / client cancelled" data-testid="void-reason" />
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="destructive" onClick={submit} disabled={voiding} data-testid="void-confirm-btn">{voiding ? 'Voiding…' : 'Void Invoice'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
